// État : Déclaration de TVA / CPS — reproduction du formulaire officiel DICP 1010.
// TOUTES les lignes sont calculées automatiquement depuis les écritures (factures).
// Les lignes 04, 08, 10, 11, 12, 16, 20, 21 (+ prorata) restent corrigeables
// manuellement jusqu'à la validation : un champ corrigé est surligné (orange) et
// peut être réinitialisé au calcul auto. Les lignes calculées non modifiables
// portent un cadenas discret. Une déclaration validée devient immuable.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { telechargerExport } from "@/lib/exportFichier";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Percent, Download, Printer, Loader2, AlertTriangle, Lock, CheckCircle2, FileText, RotateCcw,
} from "lucide-react";

const MOIS = ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
  "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];
const anneeCourante = new Date().getFullYear();
const fmt = (n) => Math.round(n || 0).toLocaleString("fr-FR");

function calculerEcheances(regime, exercice) {
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const liste = [];
  if (regime === "Trimestriel") {
    [[3, "T1"], [6, "T2"], [9, "T3"], [12, "T4"]].forEach(([fin, t]) =>
      liste.push({ libelle: `TVA ${t} ${exercice}`, date: new Date(exercice, fin, 15) }));
  } else {
    for (let m = 1; m <= 12; m++)
      liste.push({ libelle: `TVA ${MOIS[m - 1]} ${exercice}`, date: new Date(exercice, m, 15) });
  }
  return liste.filter((e) => e.date >= today).slice(0, regime === "Trimestriel" ? 2 : 3)
    .map((e) => ({ ...e, jours: Math.ceil((e.date - today) / 86400000), dateTexte: e.date.toLocaleDateString("fr-FR") }));
}

// --- Sous-composants de mise en page (style formulaire DICP) ---
const Section = ({ titre }) => (
  <div className="bg-[#D4820A] text-white text-center font-bold text-sm py-2 rounded">{titre}</div>
);
const SousSection = ({ titre }) => (
  <div className="bg-[#D4820A]/80 text-white text-center font-semibold text-xs py-1.5 rounded-sm">{titre}</div>
);
const Tableau = ({ entete, entetes, children }) => (
  <div className="bg-white border border-[#D4820A]/30 rounded overflow-hidden">
    {entete && <div className="bg-[#F3D9B5] text-[#1D3D3D] text-xs font-semibold px-3 py-1.5 text-center">{entete}</div>}
    {entetes && (
      <div className="grid grid-cols-[2.2rem_1fr_7rem_7rem] bg-[#F3D9B5] text-[#1D3D3D] text-xs font-semibold">
        {entetes.map((h, i) => (<div key={i} className={`px-3 py-1.5 ${i >= 2 ? "text-right" : ""}`}>{h}</div>))}
      </div>
    )}
    <div className="divide-y divide-[#D4820A]/10">{children}</div>
  </div>
);
const Ligne = ({ num, libelle, surligne, children }) => (
  <div className={`grid grid-cols-[2.2rem_1fr_8rem] items-center text-sm ${surligne ? "bg-[#fbe3c5]" : ""}`}>
    <div className="bg-[#F3D9B5] text-center font-bold text-xs py-2 self-stretch flex items-center justify-center">{num}</div>
    <div className="px-3 py-1.5 text-[#1D3D3D]">{libelle}</div>
    <div className="px-3 py-1.5 text-right">{children}</div>
  </div>
);
const LigneBT = ({ num, libelle, base, tva }) => (
  <div className="grid grid-cols-[2.2rem_1fr_7rem_7rem] items-center text-sm">
    <div className="bg-[#F3D9B5] text-center font-bold text-xs py-2 self-stretch flex items-center justify-center">{num}</div>
    <div className="px-3 py-1.5 text-[#1D3D3D]">{libelle}</div>
    <div className="px-3 py-1.5 text-right">{base}</div>
    <div className="px-3 py-1.5 text-right">{tva}</div>
  </div>
);
const LigneTotal = ({ num, libelle, children }) => (
  <div className="grid grid-cols-[2.2rem_1fr_8rem] items-center text-sm bg-[#fbe3c5] font-bold">
    <div className="bg-[#F3D9B5] text-center text-xs py-2 self-stretch flex items-center justify-center">{num}</div>
    <div className="px-3 py-1.5 text-right text-[#1D3D3D]">{libelle}</div>
    <div className="px-3 py-1.5 text-right">{children}</div>
  </div>
);

export default function DeclarationTVA() {
  const { company } = useAuth();
  const [exercices, setExercices] = useState([]);
  const [exercice, setExercice] = useState(String(anneeCourante));
  const [regime, setRegime] = useState(company?.regime_tva || "Mensuel");
  const [periodes, setPeriodes] = useState([]);
  const [periode, setPeriode] = useState("");
  const [data, setData] = useState(null);
  const [overrides, setOverrides] = useState({});
  const [chargement, setChargement] = useState(true);
  const [enregistre, setEnregistre] = useState(false);
  const hydratant = useRef(true);

  useEffect(() => {
    api.get("/exercices").then(({ data }) => {
      setExercices(data || []);
      if (data && data.length) setExercice(String(data[0].annee));
    }).catch(() => {});
  }, []);

  const chargerPeriodes = useCallback(() => {
    api.get("/etats/tva-periodes", { params: { exercice: parseInt(exercice, 10) } })
      .then(({ data }) => {
        setRegime(data.regime);
        setPeriodes(data.periodes || []);
        setPeriode((actuel) => {
          if (actuel && (data.periodes || []).some((p) => p.valeur === actuel)) return actuel;
          const avecMvt = [...(data.periodes || [])].reverse().find((p) => p.a_mouvements);
          return (avecMvt || (data.periodes || [])[(data.periodes || []).length - 1])?.valeur || "";
        });
      }).catch(() => {});
  }, [exercice]);

  useEffect(() => { chargerPeriodes(); }, [chargerPeriodes]);

  const charger = useCallback(async () => {
    if (!periode) return;
    setChargement(true);
    try {
      const { data } = await api.get("/etats/declaration-tva", {
        params: { exercice: parseInt(exercice, 10), periode },
      });
      hydratant.current = true;
      setData(data);
      setOverrides(data.overrides || {});
    } catch {
      setData(null);
    } finally {
      setChargement(false);
    }
  }, [exercice, periode]);

  useEffect(() => { charger(); }, [charger]);

  const verrouille = data?.statut === "validée";

  // Sauvegarde automatique (debounce) des surcharges manuelles → recalcul instantané
  useEffect(() => {
    if (hydratant.current) { hydratant.current = false; return; }
    if (verrouille || !periode) return;
    const t = setTimeout(async () => {
      try {
        const { data: maj } = await api.put("/etats/declaration-tva", { periode, overrides });
        setData((d) => ({ ...d, valeurs: maj.valeurs, statut: maj.statut }));
        setEnregistre(true);
        setTimeout(() => setEnregistre(false), 1500);
      } catch {
        toast.error("La sauvegarde a échoué.");
      }
    }, 500);
    return () => clearTimeout(t);
  }, [overrides, periode, verrouille]);

  // Corrige manuellement une ligne (override) ou la réinitialise au calcul auto
  const definirOverride = (cle, valeur) =>
    setOverrides((o) => ({ ...o, [cle]: valeur === "" ? 0 : Math.round(Number(valeur) || 0) }));
  const reinitialiser = (cle) =>
    setOverrides((o) => { const n = { ...o }; delete n[cle]; return n; });

  const valider = async () => {
    try {
      const { data: maj } = await api.post("/etats/declaration-tva/valider", { periode });
      setData(maj);
      chargerPeriodes();
      toast.success("Déclaration validée et verrouillée.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "La validation a échoué.");
    }
  };

  const exporter = async (format) => {
    try {
      await telechargerExport("/etats/declaration-tva/export",
        { exercice: parseInt(exercice, 10), periode }, format, `declaration_tva_${periode}`);
    } catch {
      toast.error("L'export a échoué.");
    }
  };

  const echeances = useMemo(() => calculerEcheances(regime, parseInt(exercice, 10)), [regime, exercice]);
  const prochaine = echeances[0];
  const alerte = prochaine && prochaine.jours <= 5 ? "rouge" : prochaine && prochaine.jours <= 15 ? "orange" : null;

  const v = data?.valeurs || {};
  const ent = data?.entreprise || {};
  const estModifie = (k) => Object.prototype.hasOwnProperty.call(overrides, k);

  // Ligne calculée non modifiable (cadenas discret optionnel)
  const Calc = ({ k, testid, lock = false }) => (
    <span data-testid={testid} className="inline-flex items-center justify-end gap-1 font-semibold tabular-nums">
      {lock && <Lock className="h-3 w-3 text-[#1D3D3D]/25" />}{fmt(v[k])}
    </span>
  );
  // Ligne corrigeable manuellement : surlignée si modifiée + bouton réinitialiser
  const Libre = ({ k }) => {
    const modifie = estModifie(k);
    const valeur = modifie ? overrides[k] : (v[k] ?? 0);
    return (
      <div className="flex items-center justify-end gap-1">
        {modifie && !verrouille && (
          <button type="button" data-testid={`reset-${k}`} onClick={() => reinitialiser(k)}
            title="Réinitialiser au calcul automatique"
            className="text-[#D4820A] hover:text-[#8A4A4A] transition-colors">
            <RotateCcw className="h-3.5 w-3.5" />
          </button>
        )}
        <Input type="number" data-testid={`champ-${k}`} value={valeur} disabled={verrouille}
          onChange={(e) => definirOverride(k, e.target.value)}
          className={`h-7 w-28 text-right tabular-nums px-2 py-0 disabled:opacity-70 ${modifie
            ? "bg-[#fff4e6] border-[#D4820A] ring-1 ring-[#D4820A]/40 font-semibold text-[#8A4A4A]"
            : "bg-white border-[#4A8A8A]/30"}`} />
      </div>
    );
  };

  return (
    <div className="fenua-fade-up" data-testid="page-declaration-tva">
      {alerte && !verrouille && (
        <div data-testid="alerte-echeance"
          className={`no-print mb-4 rounded-lg px-4 py-3 flex items-center gap-3 text-sm font-semibold ${alerte === "rouge" ? "bg-red-50 text-red-800 border border-red-200" : "bg-amber-50 text-amber-800 border border-amber-200"}`}>
          <AlertTriangle className={`h-5 w-5 ${alerte === "rouge" ? "text-red-600" : "text-amber-600"}`} />
          Échéance proche : {prochaine.libelle} à déposer avant le {prochaine.dateTexte} ({prochaine.jours} j).
        </div>
      )}

      <div className="no-print flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-lg bg-[#E8F4F4] flex items-center justify-center">
            <Percent className="h-6 w-6 text-[#1D3D3D]" />
          </div>
          <div>
            <h1 data-testid="titre-section" className="text-3xl font-black tracking-tight text-[#1D3D3D]">Déclaration TVA</h1>
            <p className="text-sm text-[#1D3D3D]/70">Formulaire officiel DICP 1010 · régime {regime.toLowerCase()}.</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {enregistre && <span className="text-xs text-[#4A8A8A] flex items-center gap-1"><CheckCircle2 className="h-3 w-3" /> Enregistré</span>}
          <Button variant="secondary" data-testid="export-xls" onClick={() => exporter("xls")}
            className="bg-[#E8F4F4] text-[#1D3D3D] hover:bg-[#4A8A8A]/20 border border-[#4A8A8A]/20">
            <Download className="h-4 w-4 mr-1" /> XLS
          </Button>
          <Button variant="secondary" data-testid="export-pdf" onClick={() => exporter("pdf")}
            className="bg-[#E8F4F4] text-[#1D3D3D] hover:bg-[#4A8A8A]/20 border border-[#4A8A8A]/20">
            <FileText className="h-4 w-4 mr-1" /> PDF
          </Button>
          <Button data-testid="bouton-imprimer" onClick={() => window.print()}
            className="bg-[#1D3D3D] hover:bg-[#2D5A5A] text-white">
            <Printer className="h-4 w-4 mr-1" /> Imprimer
          </Button>
        </div>
      </div>

      <div className="no-print mt-6 bg-white border border-[#4A8A8A]/20 rounded-lg p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label className="text-xs font-semibold text-[#1D3D3D]">Exercice</Label>
          <Select value={exercice} onValueChange={setExercice}>
            <SelectTrigger data-testid="filtre-exercice" className="bg-white border-[#4A8A8A]/30 h-9"><SelectValue /></SelectTrigger>
            <SelectContent>
              {(exercices.length ? exercices : [{ id: "x", annee: anneeCourante }]).map((e) => (
                <SelectItem key={e.id} value={String(e.annee)}>Exercice {e.annee}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs font-semibold text-[#1D3D3D]">Période ({regime.toLowerCase()})</Label>
          <Select value={periode} onValueChange={setPeriode}>
            <SelectTrigger data-testid="filtre-periode" className="bg-white border-[#4A8A8A]/30 h-9"><SelectValue placeholder="Choisir une période" /></SelectTrigger>
            <SelectContent>
              {periodes.map((p) => (
                <SelectItem key={p.valeur} value={p.valeur}>
                  {p.libelle}{p.statut === "validée" ? " ✓" : p.a_mouvements ? "" : " — sans mouvement"}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {verrouille && (
        <div data-testid="bandeau-valide" className="no-print mt-4 rounded-lg px-4 py-3 flex items-center gap-3 bg-[#E8F4F4] border border-[#4A8A8A]/30 text-[#1D3D3D] text-sm font-semibold">
          <Lock className="h-5 w-5 text-[#2D5A5A]" />
          Déclaration validée le {data.validee_le?.slice(0, 10)} — document verrouillé, non modifiable.
        </div>
      )}

      <div className="mt-6 zone-impression">
        <div className="bg-white border border-[#4A8A8A]/20 rounded-lg px-5 py-3 mb-4">
          <div className="flex items-center justify-between">
            <p className="font-bold text-[#1D3D3D]">{ent.raison_sociale} <span className="font-normal text-xs text-[#1D3D3D]/60">· N° Tahiti {ent.numero_tahiti}</span></p>
            <p className="text-xs font-bold text-[#1D3D3D]/70">DECL. 1010</p>
          </div>
          <p className="text-xs text-[#1D3D3D]/60">Taxe sur la valeur ajoutée et Contribution pour la solidarité — {data?.periode_libelle}</p>
        </div>

        {chargement ? (
          <div className="bg-white border border-[#4A8A8A]/20 rounded-lg p-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#4A8A8A]" /></div>
        ) : !data ? (
          <p className="bg-white border border-[#4A8A8A]/20 rounded-lg p-10 text-center text-sm text-[#1D3D3D]/60">Aucune donnée pour cette période.</p>
        ) : (
          <div data-testid="formulaire-tva" className="space-y-3">
            {data.aucun_mouvement && (
              <p data-testid="aucun-mouvement" className="bg-[#E8F4F4] text-[#1D3D3D]/70 rounded-lg px-4 py-3 text-sm">
                Aucun mouvement TVA sur cette période — déclaration néante.
              </p>
            )}

            <Section titre="I — TAXE SUR LA VALEUR AJOUTÉE (TVA)" />

            <SousSection titre="A — Opérations réalisées" />
            <div className="grid md:grid-cols-2 gap-3">
              <Tableau entete="Opérations imposables (HT)">
                <Ligne num="01" libelle="Ventes"><Calc k="01_ht" testid="valeur-01_ht" lock /></Ligne>
                <Ligne num="02" libelle="Prestations de services (= ventes pour l'instant)"><Calc k="02_ht" testid="valeur-02_ht" lock /></Ligne>
              </Tableau>
              <Tableau entete="Opérations non imposables">
                <Ligne num="03" libelle="Exportations (0%)"><Calc k="03_ht" testid="valeur-03_ht" lock /></Ligne>
                <Ligne num="04" libelle="Autres opérations non taxables"><Libre k="04_ht" /></Ligne>
              </Tableau>
            </div>

            <SousSection titre="B — TVA exigible" />
            <Tableau entetes={["", "", "Base HT", "TVA due"]}>
              <LigneBT num="05" libelle="Taux réduit 5 %" base={<Calc k="05_base" testid="valeur-05_base" />} tva={<Calc k="05_tva" testid="valeur-05_tva" />} />
              <LigneBT num="06" libelle="Taux intermédiaire 13 %" base={<Calc k="06_base" testid="valeur-06_base" />} tva={<Calc k="06_tva" testid="valeur-06_tva" />} />
              <LigneBT num="07" libelle="Taux normal 16 %" base={<Calc k="07_base" testid="valeur-07_base" />} tva={<Calc k="07_tva" testid="valeur-07_tva" />} />
              <LigneBT num="08" libelle="Régularisation : autre TVA à reverser" base={null} tva={<Libre k="08_reg" />} />
              <LigneTotal num="09" libelle="Total TVA exigible (lignes 05 à 08)"><Calc k="09_total" testid="valeur-09_total" /></LigneTotal>
            </Tableau>

            <SousSection titre="C — TVA déductible" />
            <Tableau>
              <Ligne num="10" libelle="TVA sur biens constituant des immobilisations (comptes classe 2)"><Libre k="10_immo" /></Ligne>
              <Ligne num="11" libelle="TVA sur autres biens et services"><Libre k="11_tva" /></Ligne>
              <Ligne num="12" libelle="Régularisation : autre TVA à déduire"><Libre k="12_reg" /></Ligne>
              <Ligne num="13" libelle="Report de crédit (ligne 17 de la précédente déclaration — automatique)"><Calc k="13_report" testid="valeur-13_report" lock /></Ligne>
              <LigneTotal num="14" libelle="Total TVA déductible (lignes 10 à 13)"><Calc k="14_total" testid="valeur-14_total" /></LigneTotal>
              <Ligne num="" libelle="Prorata de déduction applicable pour la période">
                <div className="flex items-center justify-end gap-1"><Libre k="prorata" /><span className="text-xs">%</span></div>
              </Ligne>
            </Tableau>

            <div className="grid md:grid-cols-2 gap-3">
              <Tableau entete="D — Crédit">
                <Ligne num="15" libelle="Crédit de TVA (lignes 14 − 09)"><Calc k="15_credit" testid="valeur-15_credit" lock /></Ligne>
                <Ligne num="16" libelle="Remboursement demandé"><Libre k="16_remboursement" /></Ligne>
                <Ligne num="17" libelle="Crédit à reporter (lignes 15 − 16)"><Calc k="17_report" testid="valeur-17_report" lock /></Ligne>
              </Tableau>
              <Tableau entete="E — TVA à payer">
                <Ligne num="18" libelle="TVA nette due (lignes 09 − 14)" surligne={v["18_due"] > 0}><Calc k="18_due" testid="valeur-18_due" lock /></Ligne>
              </Tableau>
            </div>

            <Section titre="II — CONTRIBUTION POUR LA SOLIDARITÉ (CPS)" />
            <p className="text-xs text-[#1D3D3D]/60 px-1">Section affichée pour conformité au formulaire DICP 1010 — non calculée pour l'instant et exclue de la somme due.</p>
            <Tableau entetes={["", "", "Base HT (5 à 7)", "CPS due"]}>
              <LigneBT num="19" libelle="Taux unique 1 %" base={<Calc k="19_base" testid="valeur-19_base" lock />} tva={<Calc k="19_cps" testid="valeur-19_cps" lock />} />
              <Ligne num="20" libelle="Régularisation : CPS à reverser"><Libre k="20_reverser" /></Ligne>
              <Ligne num="21" libelle="Régularisation : CPS en diminution"><Libre k="21_diminution" /></Ligne>
              <LigneTotal num="22" libelle="CPS nette due"><Calc k="22_due" testid="valeur-22_due" /></LigneTotal>
            </Tableau>

            <div data-testid="somme-due" className={`rounded-lg px-5 py-4 flex items-center justify-between gap-4 ${v["23_somme_due"] > 0 ? "bg-[#8A4A4A] text-white" : "bg-[#E8F4F4] text-[#1D3D3D]"}`}>
              <div>
                <p className="text-sm font-bold">SOMME DUE (= TVA nette, ligne 18)</p>
                <p className="text-xs opacity-70">{v["23_somme_due"] > 0 ? "TVA à reverser à la DICP." : "Aucun versement dû pour cette période."}</p>
              </div>
              <p data-testid="valeur-23_somme_due" className="text-2xl font-black tabular-nums">{fmt(v["23_somme_due"])} XPF</p>
            </div>

            <div className="bg-white border border-[#4A8A8A]/20 rounded-lg px-4 py-3 text-xs text-[#1D3D3D]/70">
              <p className="font-semibold text-[#1D3D3D] mb-1">Moyens de paiement</p>
              Chèque à l'ordre du Trésor Public, espèces, ou virement au bénéfice de la Recette des Impôts.<br />
              IEOM : 45189 00003 61100000045 · IBAN : FR7645189000036110000000 45
            </div>

            {!verrouille && (
              <div className="no-print flex justify-end pt-2">
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button data-testid="bouton-valider" className="bg-[#1D3D3D] hover:bg-[#2D5A5A] text-white">
                      <Lock className="h-4 w-4 mr-1" /> Valider la déclaration
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent data-testid="dialog-validation">
                    <AlertDialogHeader>
                      <AlertDialogTitle>Valider définitivement la déclaration ?</AlertDialogTitle>
                      <AlertDialogDescription>
                        Une fois validée, la déclaration {data.periode_libelle} sera <strong>verrouillée et non modifiable</strong>
                        {" "}(comme un arrêté de caisse). Vous pourrez toujours la consulter, l'imprimer et l'exporter.
                        Somme due : <strong>{fmt(v["23_somme_due"])} XPF</strong>.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel data-testid="annuler-validation">Annuler</AlertDialogCancel>
                      <AlertDialogAction data-testid="confirmer-validation" onClick={valider} className="bg-[#1D3D3D] hover:bg-[#2D5A5A]">
                        Valider et verrouiller
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            )}

            {echeances.length > 0 && (
              <div data-testid="echeances" className="no-print bg-white border border-[#4A8A8A]/20 rounded-lg p-4">
                <p className="text-sm font-semibold text-[#1D3D3D] mb-2">Prochaines échéances</p>
                <ul className="space-y-1 text-sm">
                  {echeances.map((e) => (
                    <li key={e.libelle} className="flex items-center justify-between text-[#1D3D3D]/80">
                      <span>{e.libelle} — à déposer avant le {e.dateTexte}</span>
                      <span className={`text-xs font-medium ${e.jours <= 5 ? "text-red-600" : e.jours <= 15 ? "text-amber-600" : "text-[#4A8A8A]"}`}>
                        {e.jours <= 0 ? "Aujourd'hui" : `dans ${e.jours} j`}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
